import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { AxiosError } from "axios";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Label,
  PasswordInput,
} from "@lsf/ui";
import { useAuth } from "@/lib/auth/AuthProvider";

interface FieldErrors {
  password?: string[];
  password_confirmation?: string[];
  email?: string[];
}

export function ResetPasswordPage() {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const email = searchParams.get("email") ?? "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [generalError, setGeneralError] = useState<string | null>(null);

  if (!token || !email) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md">
          <Card className="shadow-xl">
            <CardHeader className="space-y-2">
              <CardTitle className="text-2xl">Invalid reset link</CardTitle>
              <CardDescription>
                This link is missing required information. Request a new one from the sign-in page.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/forgot-password" className="text-sm text-accent hover:underline">
                Request a new reset link
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    setGeneralError(null);
    setSubmitting(true);

    try {
      await resetPassword({
        token,
        email,
        password,
        password_confirmation: confirmPassword,
      });
      toast.success("Password reset. Sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 422) {
        const data = err.response.data as { errors?: FieldErrors; message?: string };
        setErrors(data.errors ?? {});
        if (!data.errors) setGeneralError(data.message ?? "Could not reset password.");
      } else {
        setGeneralError("Could not reset password. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-xl">
          <CardHeader className="space-y-2">
            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
              <KeyRound className="w-6 h-6 text-accent" />
            </div>
            <CardTitle className="text-2xl">Set a new password</CardTitle>
            <CardDescription>Choose a new password for your account.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              {generalError && (
                <div
                  role="alert"
                  className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {generalError}
                </div>
              )}
              {errors.email?.map((msg) => (
                <div
                  key={msg}
                  role="alert"
                  className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {msg}
                </div>
              ))}

              <PasswordField
                id="new"
                label="New password"
                autoComplete="new-password"
                value={password}
                onChange={setPassword}
                errors={errors.password}
                hint="At least 10 characters, mixed case, a number, and a symbol."
              />
              <PasswordField
                id="confirm"
                label="Confirm new password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                errors={errors.password_confirmation}
              />

              <Button type="submit" disabled={submitting} className="w-full h-11 mt-2">
                {submitting ? "Resetting…" : "Reset password"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function PasswordField({
  id,
  label,
  value,
  onChange,
  errors,
  hint,
  autoComplete,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  errors?: string[];
  hint?: string;
  autoComplete?: string;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={`pw-${id}`} className="text-sm font-medium">
        {label}
      </Label>
      <PasswordInput
        id={`pw-${id}`}
        autoComplete={autoComplete}
        required
        className="h-11"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!errors?.length}
      />
      {hint && !errors?.length && <p className="text-xs text-muted-foreground">{hint}</p>}
      {errors?.map((msg) => (
        <p key={msg} className="text-xs text-destructive">
          {msg}
        </p>
      ))}
    </div>
  );
}
